<script setup lang="ts">
// Etapa 2 dos provedores que pagam por página externa embutida: AstroPay
// (`astropay_response.url`), Worldline (`payment_link`) e cartão de crédito
// (`checkout_page`). O fluxo antigo tem um componente por provedor
// (components/deposit/result/{AstroPay,Worldline,CreditCard}.vue), mas os três
// são a mesma tela: iframe + "voltar" + "já paguei". Aqui é um só, com a URL
// resolvida por método.

type Props = {
  deposit: DefaultDepositInfoResponse;
  method: string;
};

const { deposit, method } = defineProps<Props>();

type Emits = {
  back: [];
  paid: [];
};

const emit = defineEmits<Emits>();

// Cada provedor devolve a URL num campo diferente da MESMA resposta.
const checkoutUrl = computed<string>(() => {
  if (method === 'astropay') return deposit.astropay_response?.url || '';

  if (method === 'credit-card' || method === 'prontopaga') {
    return String(deposit.checkout_page || '');
  }

  return deposit.payment_link || '';
});

// Rótulos por provedor: as chaves de astropay servem o cartão de crédito
// também, como no fluxo antigo (CreditCard.vue usa deposit.astropay.*).
const labels = computed(() =>
  method === 'worldline'
    ? { back: 'deposit.worldline.go_back', paid: 'deposit.worldline.paid' }
    : { back: 'deposit.astropay.go_back', paid: 'deposit.astropay.paid' },
);
</script>

<template>
  <div class="gdm-checkout">
    <div
      v-if="checkoutUrl"
      class="gdm-checkout-frame"
    >
      <iframe
        frameborder="0"
        :src="checkoutUrl"
        :title="$t('deposit.general.modal_title')"
      ></iframe>
    </div>

    <div
      v-else
      class="gdm-checkout-error"
      role="alert"
    >
      {{ $t('deposit.errors.processing_error') }}
    </div>

    <div class="gdm-checkout-actions">
      <button
        class="gdm-checkout-back"
        type="button"
        @click="emit('back')"
      >
        &#8592; {{ $t(labels.back) }}
      </button>

      <button
        class="gdm-checkout-paid"
        type="button"
        @click="emit('paid')"
      >
        {{ $t(labels.paid) }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
$orange: #ff3700;
$border: #28282c;

.gdm-checkout {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-checkout-frame {
  position: relative;
  width: 100%;
  height: 420px;
  overflow: hidden;
  border: 1px solid $border;
  border-radius: 12px;
  background: #fff;

  iframe {
    width: 100%;
    height: 100%;
  }
}

.gdm-checkout-error {
  width: 100%;
  padding: 12px 11px;
  border-radius: 12px;
  background: rgba(255, 56, 60, 0.15);
  color: #ef4444;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}

.gdm-checkout-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.gdm-checkout-back {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 1px solid $border;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.gdm-checkout-paid {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 12px;
  background: $orange;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
</style>
